import { Suspense, useEffect } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  BrowserRouter as Router,
} from 'react-router-dom';

import { ConverterStatus, IConverter } from '../hooks/converterBase/types';
import Loader from '../components/core/Loader';

import { ConvertorAppRoutesSimple } from './types';

function SuspenseLoader() {
  return (
    <div
      style={{
        display: 'flex',
        width: '100vw',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Loader />
    </div>
  );
}

interface IConverterSimpleAppRouter<T extends IConverter<any, any>> {
  baseUrl: string;
  converter: T;
  Home: React.FC<{ converter: T }>;
  Preview?: React.FC<{ converter: T }>;
  Results?: React.FC<{ converter: T }>;
  Empty?: React.FC<{ converter: T }>;
  Error?: React.FC<{ converter: T }>;
}

function ConverterSimpleAppRouter<T extends IConverter<any, any>>({
  baseUrl,
  converter,
  Home,
  Preview,
  Empty,
}: IConverterSimpleAppRouter<T>) {
  return (
    <Router basename={baseUrl}>
      <ConverterSimpleAppRoutes converter={converter} Home={Home} Preview={Preview} Empty={Empty} />
    </Router>
  );
}

export function ConverterSimpleAppRoutes<T extends IConverter<any, any>>({
  converter,
  Home,
  Preview,
  Empty,
}: Pick<IConverterSimpleAppRouter<T>, 'converter' | 'Home' | 'Preview' | 'Empty'>) {
  const { search } = useLocation();
  const history = useHistory();

  (window as any).converter = converter;

  useEffect(() => {
    const status = converter.status;

    if (status === ConverterStatus.INIT) {
      history.replace(`${ConvertorAppRoutesSimple.HOME}${search}`);
    } else if (status === ConverterStatus.PROCESSED) {
      history.replace(`${ConvertorAppRoutesSimple.PREVIEW}${search}`);
    } else if (status === ConverterStatus.EMPTY) {
      history.replace(`${ConvertorAppRoutesSimple.EMPTY}${search}`);
    }
  }, [converter.status, history, search]);

  return (
    <Suspense fallback={<SuspenseLoader />}>
      <Switch>
        <Route path={`${ConvertorAppRoutesSimple.PREVIEW}`}>
          <Preview converter={converter} />
        </Route>

        <Route path={`${ConvertorAppRoutesSimple.EMPTY}`}>
          <Empty converter={converter} />
        </Route>

        <Route path={`${ConvertorAppRoutesSimple.HOME}`}>
          <Home converter={converter} />
        </Route>

        <Redirect
          to={{
            pathname: `${ConvertorAppRoutesSimple.HOME}`,
            search: search,
          }}
        />
      </Switch>
    </Suspense>
  );
}

export default ConverterSimpleAppRouter;
